
import React, { useState, useEffect, useMemo, memo } from 'react';
import { Transaction } from '../types';
import { CategoryIcon } from './icons';

interface CategorizationViewProps {
    transactionsToReview: Transaction[];
    categories: string[];
    onConfirmCategory: (transactionId: string, newCategory: string) => void;
    onBulkConfirmCategory: (transactionIds: string[], newCategory: string) => void;
    onFinishReview: () => void;
    getCategoryFromAI: (concept: string, counterparty: string) => Promise<string[]>;
    onAddCategory: (newCategory: string) => void;
}

const TransactionReviewCard: React.FC<{
    transaction: Transaction;
    categories: string[];
    onConfirmCategory: (transactionId: string, newCategory: string) => void;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onAddCategory: (newCategory: string) => void;
    suggestion: string;
    isLoading: boolean;
}> = memo(({ transaction, categories, onConfirmCategory, isSelected, onSelect, onAddCategory, suggestion, isLoading }) => {
    
    const [selectedCategory, setSelectedCategory] = useState(suggestion);

    useEffect(() => {
        // Update local state when the suggestion prop changes
        setSelectedCategory(suggestion);
    }, [suggestion]);
    
    const handleConfirm = () => {
        if (selectedCategory && selectedCategory !== "Desconocida") {
            onConfirmCategory(transaction.id, selectedCategory);
        }
    };
    
    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;
        if (value === '__CREATE_NEW__') {
            const newCategoryName = window.prompt("Introduce el nombre de la nueva categoría:");
            if (newCategoryName && newCategoryName.trim() !== '') {
                const trimmedName = newCategoryName.trim();
                onAddCategory(trimmedName);
                setSelectedCategory(trimmedName);
            }
        } else {
            setSelectedCategory(value);
        }
    };

    return (
        <div className={`bg-light-surface dark:bg-gray-800 p-4 rounded-lg flex flex-col md:flex-row items-start md:items-center gap-4 transition-colors duration-200 ${isSelected ? 'ring-2 ring-primary' : 'border border-light-border dark:border-transparent'}`}>
            <div className="flex items-center w-full md:w-auto">
                 <input 
                    type="checkbox" 
                    checked={isSelected}
                    onChange={() => onSelect(transaction.id)}
                    className="h-5 w-5 rounded border-gray-400 dark:border-gray-500 bg-gray-200 dark:bg-gray-700 text-primary focus:ring-primary mr-4 cursor-pointer"
                />
                <div className="flex-grow">
                    <p className="text-xs text-light-text-secondary dark:text-text-secondary">{transaction.date}</p>
                    <p className="font-medium text-light-text-primary dark:text-text-primary">{transaction.concept}</p>
                    <p className="text-sm text-light-text-secondary dark:text-text-secondary">{transaction.counterparty}</p>
                </div>
            </div>

            <div className="flex-grow flex flex-col md:flex-row items-start md:items-center gap-3 w-full md:w-auto md:ml-auto">
                 <p className={`font-bold text-lg md:text-right w-full md:w-32 ${transaction.amount > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {transaction.amount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </p>
                {isLoading ? (
                    <div className="w-full md:w-56 h-10 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse"></div>
                ) : (
                    <select
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        className="w-full md:w-56 bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary appearance-none"
                    >
                        {selectedCategory === "Desconocida" && <option value="Desconocida">Elige una categoría</option>}
                        {categories.filter(c => c !== "Desconocida").sort().map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                         <option value="" disabled>──────────────────</option>
                         <option value="__CREATE_NEW__">-- Crear nueva categoría --</option>
                         <option value="Desconocida">Desconocida</option>
                    </select>
                )}
                <button 
                    onClick={handleConfirm}
                    disabled={isLoading || !selectedCategory || selectedCategory === "Desconocida"}
                    className="w-full md:w-auto px-4 py-2 bg-secondary text-white rounded-lg hover:bg-green-600 transition-colors duration-200 font-medium shadow-sm disabled:bg-gray-500 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                    Confirmar
                </button>
            </div>
        </div>
    );
});


const CategorizationView: React.FC<CategorizationViewProps> = ({ transactionsToReview, categories, onConfirmCategory, onBulkConfirmCategory, onFinishReview, getCategoryFromAI, onAddCategory }) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [bulkCategory, setBulkCategory] = useState('');
    const [suggestions, setSuggestions] = useState<Map<string, { suggestion: string; isLoading: boolean }>>(new Map());
    
    const totalTransactions = useMemo(() => transactionsToReview.length, [transactionsToReview]);

    useEffect(() => {
        // 1. Find transactions that don't have a suggestion yet.
        const transactionsToFetch = transactionsToReview.filter(t => !suggestions.has(t.id));

        // 2. If all transactions already have suggestions, do nothing.
        if (transactionsToFetch.length === 0) {
            return;
        }

        // 3. Mark new transactions as loading to provide immediate feedback.
        setSuggestions(prevSuggestions => {
            const newSuggestions = new Map(prevSuggestions);
            transactionsToFetch.forEach(transaction => {
                newSuggestions.set(transaction.id, { suggestion: 'Desconocida', isLoading: true });
            });
            return newSuggestions;
        });

        // 4. Fetch suggestions for each new transaction individually and update state as each one completes.
        transactionsToFetch.forEach(async (transaction) => {
            try {
                const [aiSuggestion] = await getCategoryFromAI(transaction.concept, transaction.counterparty);
                setSuggestions(prevSuggestions => {
                    const newSuggestions = new Map(prevSuggestions);
                    newSuggestions.set(transaction.id, { suggestion: aiSuggestion, isLoading: false });
                    return newSuggestions;
                });
            } catch (error) {
                console.error(`Failed to get suggestion for ${transaction.id}`, error);
                setSuggestions(prevSuggestions => {
                    const newSuggestions = new Map(prevSuggestions);
                    newSuggestions.set(transaction.id, { suggestion: 'Desconocida', isLoading: false });
                    return newSuggestions;
                });
            }
        });
    }, [transactionsToReview, getCategoryFromAI, suggestions]);


    const handleSelect = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };
    
    const handleSelectAll = () => {
        if (selectedIds.length === transactionsToReview.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(transactionsToReview.map(t => t.id));
        }
    };
    
    const handleBulkApply = () => {
        if (bulkCategory && bulkCategory.trim() !== '' && selectedIds.length > 0) {
            onBulkConfirmCategory(selectedIds, bulkCategory);
            setSelectedIds([]);
            setBulkCategory('');
        }
    };

    if (totalTransactions === 0) {
        return (
            <div className="bg-light-surface dark:bg-surface p-8 rounded-lg border border-light-border dark:border-border shadow-md text-center">
                <CategoryIcon className="w-16 h-16 text-secondary mx-auto mb-4"/>
                <h3 className="font-bold text-2xl text-light-text-primary dark:text-text-primary mb-2">¡Todo categorizado!</h3>
                <p className="text-light-text-secondary dark:text-text-secondary mb-6">Buen trabajo. No hay más transacciones por revisar.</p>
                <button onClick={onFinishReview} className="bg-primary text-white font-semibold px-6 py-2.5 rounded-lg hover:bg-blue-600 transition-colors">
                    Volver al Panel
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-light-surface dark:bg-surface p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h3 className="font-bold text-xl text-light-text-primary dark:text-text-primary">Centro de Categorización</h3>
                        <p className="text-light-text-secondary dark:text-text-secondary">Revisa las transacciones pendientes para mejorar tus informes.</p>
                    </div>
                     <button onClick={onFinishReview} className="text-sm font-medium text-primary hover:underline">
                        Terminar revisión
                    </button>
                </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedIds.length > 0 && (
                <div className="sticky top-4 z-10 bg-gray-200 dark:bg-gray-800 p-4 rounded-lg border border-light-border dark:border-border shadow-lg flex flex-col sm:flex-row items-center gap-4">
                    <p className="font-medium text-light-text-primary dark:text-text-primary flex-shrink-0">{selectedIds.length} transacciones seleccionadas</p>
                    <div className="flex-grow flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <input
                            list="category-list"
                            value={bulkCategory}
                            onChange={e => setBulkCategory(e.target.value)}
                            className="w-full sm:w-64 bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="Asignar categoría en bloque"
                        />
                        <button onClick={handleBulkApply} disabled={!bulkCategory || bulkCategory.trim() === ''} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 font-semibold disabled:bg-gray-600">
                            Aplicar
                        </button>
                    </div>
                </div>
            )}
            
            <div className="space-y-3">
                 <div className="flex items-center px-4 py-2">
                    <input 
                        type="checkbox"
                        checked={selectedIds.length > 0 && selectedIds.length === transactionsToReview.length}
                        onChange={handleSelectAll}
                        className="h-5 w-5 rounded border-gray-400 dark:border-gray-500 bg-gray-200 dark:bg-gray-700 text-primary focus:ring-primary mr-4 cursor-pointer"
                        aria-label="Seleccionar todo"
                    />
                    <label onClick={handleSelectAll} className="cursor-pointer text-sm font-medium text-light-text-secondary dark:text-text-secondary">Seleccionar todo</label>
                </div>
                {transactionsToReview.map(transaction => {
                    const suggestionState = suggestions.get(transaction.id) || { suggestion: 'Desconocida', isLoading: true };
                    return (
                        <TransactionReviewCard 
                            key={transaction.id}
                            transaction={transaction}
                            categories={categories}
                            onConfirmCategory={onConfirmCategory}
                            isSelected={selectedIds.includes(transaction.id)}
                            onSelect={handleSelect}
                            onAddCategory={onAddCategory}
                            suggestion={suggestionState.suggestion}
                            isLoading={suggestionState.isLoading}
                        />
                    );
                })}
            </div>
            <datalist id="category-list">
                {categories.sort().map(cat => <option key={cat} value={cat} />)}
            </datalist>
        </div>
    );
};

export default CategorizationView;
